(() => {
  const isBoxAvailableForQty = (box, qty) => {
    if (!box?.available) return false;
    if (box.inventoryManagement === 'shopify' && box.inventoryPolicy === 'deny') {
      return box.inventoryQuantity >= qty;
    }
    return true;
  };

  const getLineItemProperties = (form) => {
    const properties = {};
    for (const [key, value] of new FormData(form).entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value === '') continue;
      properties[match[1]] = value;
    }
    return properties;
  };

  const initGiftBoxModals = () => {
    document.querySelectorAll('[id^="gift-box-config-"]').forEach((configElement) => {
      let config;
      try {
        config = JSON.parse(configElement.textContent);
      } catch {
        return;
      }

      const selectModal = document.getElementById(config.selectModalId);
      if (!selectModal || typeof selectModal.show !== 'function') return;

      const sectionRoot = configElement.closest('.shopify-section') || document;
      const optionsRoot = selectModal.querySelector('[data-gift-box-options]');
      const confirmButton = selectModal.querySelector('[data-gift-box-confirm]');
      const choiceName = optionsRoot?.querySelector('input[type="radio"]')?.name;
      let pendingForm = null;

      const getProductForm = (element) => {
        if (element instanceof HTMLFormElement && element.matches('form[is="product-form"]')) {
          return element;
        }

        const formId = element.getAttribute?.('form');
        if (formId) {
          const linkedForm = document.getElementById(formId);
          if (linkedForm instanceof HTMLFormElement && linkedForm.matches('form[is="product-form"]')) {
            return linkedForm;
          }
        }

        return element.closest?.('form[is="product-form"]') ?? null;
      };

      const getQuantity = (form) => {
        const input = form?.elements?.namedItem('quantity');
        const value = input && 'value' in input ? parseInt(input.value, 10) : 1;
        return Number.isFinite(value) && value > 0 ? value : 1;
      };

      const getSelectedChoice = () => {
        const checked = optionsRoot?.querySelector(`input[name="${choiceName}"]:checked`);
        return checked?.value || 'none';
      };

      const setChoice = (value) => {
        const input = optionsRoot?.querySelector(`input[name="${choiceName}"][value="${value}"]`);
        if (input && !input.disabled) input.checked = true;
      };

      const pickDefaultChoice = (qty) => {
        const aOk = isBoxAvailableForQty(config.boxes.a, qty);
        const bOk = isBoxAvailableForQty(config.boxes.b, qty);
        if (aOk && !bOk) return 'a';
        if (bOk && !aOk) return 'b';
        return 'none';
      };

      const updateOptionStates = (form) => {
        const qty = getQuantity(form);

        ['a', 'b'].forEach((key) => {
          const option = optionsRoot?.querySelector(`[data-gift-box-option="${key}"]`);
          const input = option?.querySelector('input[type="radio"]');
          if (!option || !input) return;

          const available = isBoxAvailableForQty(config.boxes[key], qty);
          input.disabled = !available;
          option.classList.toggle('gift-box-option--disabled', !available);
        });

        const current = getSelectedChoice();
        const currentInput = optionsRoot?.querySelector(`input[name="${choiceName}"][value="${current}"]`);
        if (!currentInput || currentInput.disabled) {
          setChoice(pickDefaultChoice(qty));
        }
      };

      sectionRoot.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('form[is="product-form"]')) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        pendingForm = form;
        updateOptionStates(form);
        selectModal.show(false);
      }, true);

      sectionRoot.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.name !== 'quantity') return;

        const form = getProductForm(target);
        if (form && pendingForm === form && selectModal.open) {
          updateOptionStates(form);
        }
      });

      confirmButton?.addEventListener('click', async () => {
        if (!pendingForm) return;

        const choice = getSelectedChoice();
        const qty = getQuantity(pendingForm);
        const variantId = parseInt(pendingForm.querySelector('[name="id"]')?.value, 10);
        if (!variantId) return;

        const mainItem = { id: variantId, quantity: qty };
        const properties = getLineItemProperties(pendingForm);
        if (Object.keys(properties).length) {
          mainItem.properties = properties;
        }

        const items = [mainItem];
        if (choice === 'a') items.push({ id: config.boxes.a.variantId, quantity: qty });
        if (choice === 'b') items.push({ id: config.boxes.b.variantId, quantity: qty });

        confirmButton.setAttribute('aria-busy', 'true');
        confirmButton.disabled = true;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          const responseJson = await response.json();
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

          if (response.ok) {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }

          pendingForm.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description || responseJson.message },
          }));
          await selectModal.hide();
        } catch {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          pendingForm.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: 'Cart add failed' },
          }));
          await selectModal.hide();
        } finally {
          confirmButton.removeAttribute('aria-busy');
          confirmButton.disabled = false;
        }
      });
    });
  };

  const boot = () => customElements.whenDefined('x-modal').then(initGiftBoxModals);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
